﻿import React from 'react';

/**
 * Парсит текст с markdown-lite разметкой и возвращает массив React-элементов.
 * Поддерживает:
 *  - `*bold*` → <strong>
 *  - `_italic_` → <em>
 *  - `` `code` `` → <code>
 *
 * Порядок обработки: code → bold → italic.
 * Вложенные конструкции не поддерживаются.
 *
 * @param {string} text — исходный текст с разметкой
 * @returns {Array<React.ReactNode>} — массив React-нод для рендера
 */
export function renderMarkdownLite(text) {
    if (!text) return [text];

    // 1. Извлекаем все `code` (самые приоритетные)
    const codeRegex = /(`+)((?:[^`]|(?!\1)[\s\S])*?)\1/g;
    const codeParts = [];
    let lastIndex = 0;
    let match;

    while ((match = codeRegex.exec(text)) !== null) {
        if (match.index > lastIndex) {
            codeParts.push({ type: 'text', value: text.slice(lastIndex, match.index) });
        }
        codeParts.push({ type: 'code', value: match[2] });
        lastIndex = match.index + match[0].length;
    }
    if (lastIndex < text.length) {
        codeParts.push({ type: 'text', value: text.slice(lastIndex) });
    }
    if (codeParts.length === 0) {
        codeParts.push({ type: 'text', value: text });
    }

    // 2. В текстовых фрагментах парсим *bold*
    const boldParts = codeParts.flatMap(part => {
        if (part.type !== 'text') return [part];
        const result = [];
        const boldRegex = /\*([^*]+)\*/g;
        let idx = 0;
        let m;
        while ((m = boldRegex.exec(part.value)) !== null) {
            if (m.index > idx) {
                result.push({ type: 'text', value: part.value.slice(idx, m.index) });
            }
            result.push({ type: 'bold', value: m[1] });
            idx = m.index + m[0].length;
        }
        if (idx < part.value.length) {
            result.push({ type: 'text', value: part.value.slice(idx) });
        }
        if (result.length === 0) result.push(part);
        return result;
    });

    // 3. В текстовых фрагментах парсим _italic_
    const italicParts = boldParts.flatMap(part => {
        if (part.type !== 'text') return [part];
        const result = [];
        const italicRegex = /_([^_]+)_/g;
        let idx = 0;
        let m;
        while ((m = italicRegex.exec(part.value)) !== null) {
            if (m.index > idx) {
                result.push({ type: 'text', value: part.value.slice(idx, m.index) });
            }
            result.push({ type: 'italic', value: m[1] });
            idx = m.index + m[0].length;
        }
        if (idx < part.value.length) {
            result.push({ type: 'text', value: part.value.slice(idx) });
        }
        if (result.length === 0) result.push(part);
        return result;
    });

    // 4. В текстовых фрагментах парсим @mentions
    const mentionParts = italicParts.flatMap(part => {
        if (part.type !== 'text') return [part];
        const result = [];
        // @Name — буквы, цифры, пробелы, дефис, подчёркивание, точка (до 40 символов)
        const mentionRegex = /@([\p{L}\p{N}][\p{L}\p{N}\s._-]{0,39})/gu;
        let idx = 0;
        let m;
        while ((m = mentionRegex.exec(part.value)) !== null) {
            if (m.index > idx) {
                result.push({ type: 'text', value: part.value.slice(idx, m.index) });
            }
            result.push({ type: 'mention', value: m[0] });
            idx = m.index + m[0].length;
        }
        if (idx < part.value.length) {
            result.push({ type: 'text', value: part.value.slice(idx) });
        }
        if (result.length === 0) result.push(part);
        return result;
    });

    // 5. Превращаем токены в React-элементы
    return mentionParts.map((part, i) => {
        switch (part.type) {
            case 'bold':
                return React.createElement('strong', { key: i }, part.value);
            case 'italic':
                return React.createElement('em', { key: i }, part.value);
            case 'code':
                return React.createElement('code', { key: i }, part.value);
            case 'mention':
                return React.createElement('span', { key: i, className: 'chat-message__mention' }, part.value);
            case 'text':
            default:
                return part.value;
        }
    });
}
